import { bangkokWallTimeToRfc3339 } from '../commission-ledger/types';
import { auditLogFixtures, securityEventFixtures } from './fixtures';
import type { AuditRepository } from './repository';
import type { AuditLogFilters, SecurityEventFilters } from './types';

// Stable, human-readable route identifiers resolve to the first fixture without duplicating
// fixture rows or altering list/pagination totals.
const auditLogDetailAliases: Readonly<Record<string, string>> = {
  'audit-a': auditLogFixtures[0]!.id,
};
const securityEventDetailAliases: Readonly<Record<string, string>> = {
  'event-a': securityEventFixtures[0]!.id,
};

export class MockAuditRepository implements AuditRepository {
  async listAuditLogs(
    query: Parameters<AuditRepository['listAuditLogs']>[0],
    _signal: AbortSignal,
  ) {
    const items = auditLogFixtures.filter((log) => matchesAuditFilters(log, query.filters));
    return { items, nextCursor: null, requestId: 'mock-req-audit-logs' };
  }

  async getAuditLog(auditId: string, _signal: AbortSignal) {
    const resolvedId = auditLogDetailAliases[auditId] ?? auditId;
    const log = auditLogFixtures.find((item) => item.id === resolvedId);
    if (log === undefined) throw new Error('Mock audit log not found');
    return log;
  }

  async listSecurityEvents(
    query: Parameters<AuditRepository['listSecurityEvents']>[0],
    _signal: AbortSignal,
  ) {
    const items = securityEventFixtures.filter((event) =>
      matchesSecurityEventFilters(event, query.filters),
    );
    return { items, nextCursor: null, requestId: 'mock-req-security-events' };
  }

  async getSecurityEvent(eventId: string, _signal: AbortSignal) {
    const resolvedId = securityEventDetailAliases[eventId] ?? eventId;
    const event = securityEventFixtures.find((item) => item.id === resolvedId);
    if (event === undefined) throw new Error('Mock security event not found');
    return event;
  }
}

function matchesAuditFilters(
  log: (typeof auditLogFixtures)[number],
  filters: AuditLogFilters,
): boolean {
  return (
    (filters.principalActorId.trim() === '' ||
      log.principalActorId === filters.principalActorId.trim()) &&
    (filters.principalRole === '' || log.principalRole === filters.principalRole) &&
    (filters.partnerId.trim() === '' || log.partnerId === filters.partnerId.trim()) &&
    (filters.agentId.trim() === '' || log.agentId === filters.agentId.trim()) &&
    (filters.requestId.trim() === '' || log.publicRequestId === filters.requestId.trim()) &&
    (filters.sourceIp.trim() === '' || log.sourceIp === filters.sourceIp.trim()) &&
    (filters.action.trim() === '' || log.action === filters.action.trim().toUpperCase()) &&
    (filters.entityType.trim() === '' ||
      log.entityType === filters.entityType.trim().toUpperCase()) &&
    (filters.entityId.trim() === '' || log.entityId === filters.entityId.trim()) &&
    timestampInRange(log.occurredAt, filters.from, filters.to)
  );
}

function matchesSecurityEventFilters(
  event: (typeof securityEventFixtures)[number],
  filters: SecurityEventFilters,
): boolean {
  return (
    (filters.principalActorId.trim() === '' ||
      event.principalActorId === filters.principalActorId.trim()) &&
    (filters.principalRole === '' || event.principalRole === filters.principalRole) &&
    (filters.partnerId.trim() === '' || event.partnerId === filters.partnerId.trim()) &&
    (filters.agentId.trim() === '' || event.agentId === filters.agentId.trim()) &&
    (filters.requestId.trim() === '' || event.publicRequestId === filters.requestId.trim()) &&
    (filters.sourceIp.trim() === '' || event.sourceIp === filters.sourceIp.trim()) &&
    (filters.credentialActorId.trim() === '' ||
      event.credentialActorId === filters.credentialActorId.trim()) &&
    (filters.category.trim() === '' || event.category === filters.category.trim().toUpperCase()) &&
    (filters.severity === '' || event.severity === filters.severity) &&
    (filters.eventCode.trim() === '' ||
      event.eventCode === filters.eventCode.trim().toUpperCase()) &&
    timestampInRange(event.occurredAt, filters.from, filters.to)
  );
}

function timestampInRange(timestamp: string, from: string, to: string): boolean {
  const instant = Date.parse(timestamp);
  const lower = filterInstant(from);
  const upper = filterInstant(to);
  return (lower === null || instant >= lower) && (upper === null || instant < upper);
}

function filterInstant(value: string): number | null {
  if (value.trim().length === 0) return null;
  return Date.parse(bangkokWallTimeToRfc3339(value));
}
